import React, { useRef, useState } from 'react';
import Button from '@mui/material/Button';
import ButtonGroup from '@mui/material/ButtonGroup';
import moment from 'moment';
import { useDispatch } from 'react-redux';

import { addToCartData } from '@actions/userActions';
import PrintIcon from '@mui/icons-material/Print';
import { logError } from '@actions/userActions';
import { printToKOTPrinter, resolveKitchenPrintChannel } from './PrintUtility';
import { ConfirmationModal } from '@components/ConfirmModal/Confirmation';
import {
  DEFAULT_ORDER_SERVICE_TYPE,
  ORDER_SERVICE_TYPE_LABELS,
  normalizeOrderServiceType,
  type OrderServiceType,
} from '@constants/orderServiceType';
import {
  buildKotLogItemsFromCart,
  logKotPrintAsync,
} from '@utilities/kotLogging';
import {
  KOT_SESSION_CART_KEY,
  ensureKotSession,
  isKotSessionCartKey,
} from '@utilities/kotSession';
import type { KotPrintSource, KotPrintType, KotPrinterChannel } from '@src/types/kotLogging';
import './Print.scss';

declare global {
    interface Window {
        ReactNativeWebView: any;
    }
}

interface SelectedVariants {
    value: number;
    label: string;
}

interface CartItem {
    name?: string;
    selected?: number;
    printed?: number;
    selectedVariants?: SelectedVariants;
    image?: string;
    tableNumber?: string;
    itemDescription?: string; // Kitchen instructions/description
    [key: string]: any; // Allow other properties
}

export interface CartData {
    [key: string]: CartItem | null;
}

/** Build kitchen KOT HTML for a set of cart lines (shared by POS cart and report reprints). */
export function getFoodKOTHTMLString(
    items: CartData,
    tableNumber: string,
    orderCreatedAt?: number,
    serviceType: OrderServiceType = DEFAULT_ORDER_SERVICE_TYPE
): string {
    if (!items || Object.keys(items).length === 0) {
        return '';
    }

    let sum = '';
    Object.entries(items).forEach(([_keyCart, itemCart]) => {
        if (itemCart && itemCart.name && itemCart.selected && itemCart.selected > 0) {
            const itemName = (itemCart.name || '').replaceAll('-', ' ');
            const variantLabel = (itemCart.selectedVariants?.label || '').replaceAll('₹', '');
            const quantity = itemCart.selected;
            const description = itemCart.itemDescription?.trim() || '';

            sum += `<tr>
                    <td style="text-align: left; padding-bottom: ${description ? '2px' : '4px'};">
                        ${itemName} (${variantLabel})
                    </td>
                    <td style="text-align: right; padding-bottom: ${description ? '2px' : '4px'}; width: 50px;">
                        x ${quantity}
                    </td>
                </tr>`;

            if (description) {
                sum += `<tr>
                        <td colspan="2" style="text-align: left; padding-bottom: 4px; padding-left: 10px; font-style: italic; font-size: 14px; color: #666;">
                            Note: ${description}
                        </td>
                    </tr>`;
            }
        }
    });

    const dateTime = orderCreatedAt
        ? moment(orderCreatedAt).local().format('DD MMM, YYYY h:mm a')
        : moment().local().format('DD MMM, YYYY h:mm a');

    const serviceLabel = ORDER_SERVICE_TYPE_LABELS[serviceType] ?? ORDER_SERVICE_TYPE_LABELS[DEFAULT_ORDER_SERVICE_TYPE];
    const serviceColor = serviceType === 'takeaway' ? '#b45309' : '#047857';
    const tableLabel = serviceType === 'takeaway' ? 'Pickup' : 'Table';

    return `
            <div style="font-size: 12px; width: 190px; color: #000; border-bottom: 1px solid #666;">
                <p style="font-size: 15px; text-align: center; font-weight: bold; margin: 0;">Kitchen Order</p>
                <p style="font-size: 14px; text-align: center; font-weight: bold; margin: 2px 0 0; color: ${serviceColor}; text-transform: uppercase; letter-spacing: 0.04em;">${serviceLabel}</p>
                <p style="font-size: 15px; text-align: center; font-weight: bold; margin: 4px 0 0;">${tableLabel}: ${tableNumber}</p>
                <p style="font-size: 15px; text-align: center; margin: 0;">${dateTime}</p>
                <table style="font-size: 16px; width: 190px; border-top: 1px solid #666; text-transform: capitalize;">
                    <thead>
                        <tr style="border-bottom: 1px solid #666;">
                            <th style="text-align: left; padding: 4px 0; font-weight: bold;">Item</th>
                            <th style="text-align: right; padding: 4px 0; font-weight: bold; width: 50px;">Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${sum || ''}
                    </tbody>
                </table>
            </div>
        `;
}

type PrintFoodOrderKotArgs = {
    cart?: CartData;
    tableNumber?: string;
    createdAt?: number;
    uoId?: string;
    orderId?: string;
    serviceType?: OrderServiceType;
    restaurantId?: string;
    source?: KotPrintSource;
    printType?: KotPrintType;
    kotSessionId?: string;
};

/** Print full food KOT for a saved order without mutating live table cart state. */
export async function printFoodOrderKOT(order: PrintFoodOrderKotArgs): Promise<void> {
    const cart = order?.cart;
    if (!cart || typeof cart !== 'object') {
        throw new Error('Nothing to print');
    }

    const validItems: CartData = {};
    Object.entries(cart).forEach(([key, val]) => {
        if (val && typeof val === 'object' && val.selected && val.selected > 0) {
            validItems[key] = val;
        }
    });

    if (Object.keys(validItems).length === 0) {
        throw new Error('Nothing to print');
    }

    const htmlContent = getFoodKOTHTMLString(
        validItems,
        order.tableNumber || '',
        order.createdAt,
        normalizeOrderServiceType(order.serviceType)
    );
    const orderIdentifier = order.uoId || order.orderId || 'unknown';
    const printResult = await printToKOTPrinter('kitchen', htmlContent, orderIdentifier);

    if (order.restaurantId) {
        logKotPrintAsync({
            restaurantId: order.restaurantId,
            tableNumber: order.tableNumber || '',
            serviceType: normalizeOrderServiceType(order.serviceType),
            printType: order.printType || 'online',
            source: order.source || 'online-orders',
            uoId: order.uoId || null,
            orderId: order.orderId || null,
            kotSessionId:
                order.kotSessionId ||
                (order.orderId ? `online_${order.orderId}` : null),
            items: buildKotLogItemsFromCart(validItems),
            printerChannel: printResult.channel,
        });
    }
}

interface PrintProps {
    item: CartData | null | undefined;
    tableNumber: string;
    resId: string;
    uoId?: string;
    orderId?: string;
    createdAt?: number;
    serviceType?: OrderServiceType;
    source?: KotPrintSource;
}

type PendingKotPrint = {
    printType: 'incremental' | 'full';
    itemsToPrint: CartData;
    dataAfterPrint: CartData;
};

function Print({ item, tableNumber, resId, uoId, orderId, createdAt, serviceType, source = 'pos-cart' }: PrintProps) {
    // Extract order metadata from item if it exists at root level (for backward compatibility)
    const orderMetadata = {
        uoId: uoId || (item as any)?.uoId,
        orderId: orderId || (item as any)?.orderId,
        createdAt: createdAt || (item as any)?.createdAt
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by useReactToPrint content callback
    const componentRef = useRef<HTMLDivElement>(null);
    const pendingPrintRef = useRef<PendingKotPrint | null>(null);
    const dispatch = useDispatch();
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmBusy, setConfirmBusy] = useState(false);

    const executeKotPrint = async (
        printType: 'incremental' | 'full',
        itemsToPrint: CartData,
        dataAfterPrint: CartData
    ) => {
        try {
            const { sessionId, sessionMeta } = ensureKotSession(
                { ...(item || {}), ...dataAfterPrint } as Record<string, unknown>,
                tableNumber
            );
            const cartAfterPrint: CartData = {
                ...dataAfterPrint,
                [KOT_SESSION_CART_KEY]: sessionMeta as any,
            };

            const htmlContent = getFoodKOTHTMLString(
                itemsToPrint,
                tableNumber,
                orderMetadata.createdAt,
                normalizeOrderServiceType(serviceType)
            );
            const orderIdentifier = orderMetadata.uoId || orderMetadata.orderId || 'unknown';
            const printResult = await printToKOTPrinter('kitchen', htmlContent, orderIdentifier);

            // Existing cart behaviour — unchanged timing relative to printToKOTPrinter return
            dispatch(addToCartData(cartAfterPrint, tableNumber, resId));

            // Logging is fire-and-forget and never blocks / rolls back cart updates
            logKotPrintAsync({
                restaurantId: resId,
                tableNumber,
                serviceType: normalizeOrderServiceType(serviceType),
                printType,
                source,
                uoId: orderMetadata.uoId || null,
                orderId: orderMetadata.orderId || null,
                kotSessionId: sessionId,
                items: buildKotLogItemsFromCart(itemsToPrint),
                printerChannel: printResult.channel,
            });
        } catch (error) {
            console.error('Error printing KOT:', error);
            dispatch(logError({ severity: 'error', message: 'Failed to print KOT' }));
            throw error;
        }
    };

    /**
     * Browser print dialog cannot distinguish Print vs Cancel reliably.
     * For browser channel only: confirm in-app first so accidental open+cancel
     * never prints, never updates cart, and never writes kot-logging.
     * WebView / Bluetooth keep one-click print (no OS cancel path).
     */
    const maybeConfirmThenPrint = async (
        printType: 'incremental' | 'full',
        itemsToPrint: CartData,
        dataAfterPrint: CartData
    ) => {
        let channel: KotPrinterChannel = 'browser';
        try {
            channel = await resolveKitchenPrintChannel();
        } catch {
            channel = 'browser';
        }

        if (channel === 'browser') {
            pendingPrintRef.current = { printType, itemsToPrint, dataAfterPrint };
            setConfirmOpen(true);
            return;
        }

        await executeKotPrint(printType, itemsToPrint, dataAfterPrint);
    };

    const handleConfirmPrint = async () => {
        if (confirmBusy) return;
        const pending = pendingPrintRef.current;
        if (!pending) {
            setConfirmOpen(false);
            return;
        }

        setConfirmBusy(true);
        try {
            await executeKotPrint(pending.printType, pending.itemsToPrint, pending.dataAfterPrint);
            pendingPrintRef.current = null;
            setConfirmOpen(false);
        } catch {
            // Error already surfaced via snackbar; keep modal open so user can retry/cancel
        } finally {
            setConfirmBusy(false);
        }
    };

    const handleCancelConfirm = () => {
        if (confirmBusy) return;
        pendingPrintRef.current = null;
        setConfirmOpen(false);
    };

    /**
     * Handles printing only new items added to cart (incremental print)
     * - Prints only items where selected > printed (new or increased items)
     * - Updates printed count to selected for ALL items (even if decreased)
     * - Handles edge cases: null items, missing properties, deleted items
     */
    const handleGeneratePdf = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!item || typeof item !== 'object' || Object.keys(item).length === 0) {
            dispatch(logError({ severity: 'info', message: "Nothing to Print :)" }));
            return;
        }

        const itemsToPrint: CartData = {};
        const dataAfterPrint: CartData = {};

        // Process each cart item
        Object.entries(item).forEach(([key, val]) => {
            if (isKotSessionCartKey(key)) {
                dataAfterPrint[key] = val as any;
                return;
            }

            // Skip null or invalid items
            if (!val || typeof val !== 'object') {
                // If item was deleted (null), mark it as null in dataAfterPrint
                dataAfterPrint[key] = null;
                return;
            }

            const selected = val.selected ?? 0;
            const printed = val.printed ?? 0;

            // Update printed count to current selected count for all items
            // This ensures correct tracking even if items were deleted or count decreased
            if (selected > 0) {
                dataAfterPrint[key] = {
                    ...val,
                    printed: selected
                };
            } else {
                // If selected is 0, remove the item
                dataAfterPrint[key] = null;
            }

            // Only add to print queue if there are new items (selected > printed)
            if (selected > printed) {
                const quantityToPrint = selected - printed;
                itemsToPrint[key] = {
                    ...val,
                    selected: quantityToPrint,
                    tableNumber
                };
            }
            // If selected <= printed, we don't print anything but still update the printed count above
        });

        // Check if there are any items to print
        if (Object.keys(itemsToPrint).length > 0) {
            try {
                await maybeConfirmThenPrint('incremental', itemsToPrint, dataAfterPrint);
            } catch {
                // snackbar already shown
            }
        } else {
            // No new items to print, but still update printed counts for correctness
            dispatch(addToCartData(dataAfterPrint, tableNumber, resId));
            dispatch(logError({ severity: 'info', message: "Nothing to Print :)" }));
        }
    };

    /**
     * Handles printing all items in cart (full print)
     * - Prints all items with their current selected count
     * - Updates printed count to selected for ALL items
     */
    const handleGeneratePdfFull = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        if (!item || typeof item !== 'object' || Object.keys(item).length === 0) {
            dispatch(logError({ severity: 'info', message: "Nothing to Print :)" }));
            return;
        }

        // Filter out null items and items with no selected count
        const validItems: CartData = {};
        const dataAfterPrint: CartData = {};

        Object.entries(item).forEach(([key, val]) => {
            if (isKotSessionCartKey(key)) {
                dataAfterPrint[key] = val as any;
                return;
            }

            if (val && typeof val === 'object' && val.selected && val.selected > 0) {
                validItems[key] = val;
                // Update printed count to current selected count
                dataAfterPrint[key] = {
                    ...val,
                    printed: val.selected
                };
            } else if (val && typeof val === 'object') {
                // Item exists but selected is 0 or missing, still update printed count
                dataAfterPrint[key] = val.selected === 0 ? null : { ...val, printed: val.selected ?? 0 };
            } else {
                // Item is null, mark as null
                dataAfterPrint[key] = null;
            }
        });

        if (Object.keys(validItems).length > 0) {
            try {
                await maybeConfirmThenPrint('full', validItems, dataAfterPrint);
            } catch {
                // snackbar already shown
            }
        } else {
            // No valid items to print, but still update printed counts for correctness
            if (Object.keys(dataAfterPrint).length > 0) {
                dispatch(addToCartData(dataAfterPrint, tableNumber, resId));
            }
            dispatch(logError({ severity: 'info', message: "Nothing to Print :)" }));
        }
    };

    return (
        <>
            <ButtonGroup variant="outlined" color="secondary" size="small">
                <Button 
                    onClick={handleGeneratePdf}
                    title="Print KOT"
                >
                    KOT
                </Button>
                <Button 
                    onClick={handleGeneratePdfFull}
                    title="Print Full"
                >
                    <PrintIcon />
                </Button>
            </ButtonGroup>
            <ConfirmationModal
                open={confirmOpen}
                title="Print KOT?"
                message="This will send the kitchen order ticket to the printer. Cancel if you opened this by mistake — nothing will be printed or logged."
                confirmLabel={confirmBusy ? 'Printing…' : 'Print KOT'}
                cancelLabel="Cancel"
                severity="info"
                onConfirm={handleConfirmPrint}
                onCancel={handleCancelConfirm}
            />
        </>
    );
}

export default Print;
